import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../../components/layout/Layout';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Shield } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminRolesPage = ({ embedded = false }) => {
  const [subAdmins, setSubAdmins] = useState([]);
  const [selectedSubAdmin, setSelectedSubAdmin] = useState('');
  const [permissions, setPermissions] = useState({
    can_manage_users: false,
    can_manage_surveyors: false,
    can_manage_functions: false,
    can_review_questions: false,
    can_manage_topics: false,
    can_approve_questions: false,
    can_view_finances: false,
    can_view_analytics: false
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchSubAdmins();
  }, []);

  useEffect(() => {
    if (selectedSubAdmin) {
      fetchPermissions(selectedSubAdmin);
    }
  }, [selectedSubAdmin]);

  const fetchSubAdmins = async () => {
    try {
      const response = await axios.get(`${API}/admin/users?role=sub_admin&per_page=100`);
      const subAdminUsers = response.data.items || [];
      setSubAdmins(subAdminUsers);
    } catch (error) {
      console.error('Failed to fetch sub-admins:', error);
      toast.error('Failed to fetch sub-admins');
    }
  };

  const fetchPermissions = async (subAdminId) => {
    try {
      const response = await axios.get(`${API}/admin/sub-admin-permissions/${subAdminId}`);
      setPermissions(response.data);
    } catch (error) {
      console.error('Failed to fetch permissions:', error);
    }
  };

  const handleSavePermissions = async () => {
    if (!selectedSubAdmin) {
      toast.error('Please select a sub-admin');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/admin/sub-admin-permissions?sub_admin_id=${selectedSubAdmin}`, permissions);
      toast.success('Permissions updated successfully');
    } catch (error) {
      console.error('Failed to update permissions:', error);
      toast.error('Failed to update permissions');
    } finally {
      setLoading(false);
    }
  };

  const permissionLabels = {
    can_manage_users: 'Manage Users',
    can_manage_surveyors: 'Manage Surveyors',
    can_manage_functions: 'Manage Functions',
    can_review_questions: 'Review Questions',
    can_manage_topics: 'Manage Topics',
    can_approve_questions: 'Approve Questions',
    can_view_finances: 'View Finances',
    can_view_analytics: 'View Analytics'
  };

  const content = (
    <div className={embedded ? '' : 'page-transition'}>
      {!embedded && (
        <Card className="mb-6">
          <CardHeader className="bg-gradient-to-r from-[#8B5CF6]/5 to-white">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-[#8B5CF6] rounded-lg">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <CardTitle className="text-2xl">Role Management</CardTitle>
                <CardDescription>Configure permissions for sub-admin roles</CardDescription>
              </div>
            </div>
          </CardHeader>
        </Card>
      )}
        
      <Card>
          <CardContent className="p-6 space-y-6">
            {/* Sub-Admin Selection */}
            <div>
              <Label htmlFor="sub-admin">Select Sub-Admin</Label>
              <Select value={selectedSubAdmin} onValueChange={setSelectedSubAdmin}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Choose a sub-admin to configure" />
                </SelectTrigger>
                <SelectContent>
                  {subAdmins.map((admin) => (
                    <SelectItem key={admin.id} value={admin.id}>
                      {admin.name} ({admin.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {subAdmins.length === 0 && (
                <p className="text-sm text-gray-500 mt-2">
                  No sub-admins found. Create users with sub-admin role first.
                </p>
              )}
            </div>

            {selectedSubAdmin && (
              <>
                <div className="border-t pt-6">
                  <h3 className="text-lg font-semibold mb-4">Permissions</h3>
                  <div className="space-y-4">
                    {Object.keys(permissionLabels).map((key) => (
                      <div key={key} className="flex items-center justify-between p-4 border rounded-lg">
                        <Label htmlFor={key} className="cursor-pointer">
                          {permissionLabels[key]}
                        </Label>
                        <Switch
                          id={key}
                          checked={permissions[key]}
                          onCheckedChange={(checked) =>
                            setPermissions({ ...permissions, [key]: checked })
                          }
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex justify-end pt-4">
                  <Button
                    onClick={handleSavePermissions}
                    disabled={loading}
                    className="bg-[#8B5CF6] hover:bg-[#8B5CF6]/90"
                  >
                    {loading ? 'Saving...' : 'Save Permissions'}
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
    </div>
  );

  return embedded ? content : <Layout>{content}</Layout>;
};

export default AdminRolesPage;
